import { useCallback } from "react";
import { useAutosaveState } from "./use-autosave-state";

export interface UseAutosaveHandlerOptions<TValues> {
  entityId: string | null;
  canEdit: boolean;
  handleSubmit: (values: TValues) => Promise<void>;
}

export function useAutosaveHandler<TValues>({ entityId, canEdit, handleSubmit }: UseAutosaveHandlerOptions<TValues>) {
  const { isAutosavePaused, setIsAutosavePaused, saveStatus, handleSaveStart, handleSaveComplete, handleSaveError } =
    useAutosaveState();

  const handleAutosave = useCallback(
    async (values: TValues) => {
      if (!entityId || !canEdit || isAutosavePaused) {
        return;
      }

      handleSaveStart();
      try {
        await handleSubmit(values);
        handleSaveComplete();
      } catch {
        handleSaveError();
      }
    },
    [entityId, canEdit, isAutosavePaused, handleSubmit, handleSaveStart, handleSaveComplete, handleSaveError]
  );

  const shouldWarnBeforeLeaving = isAutosavePaused && canEdit && !!entityId;

  return {
    isAutosavePaused,
    setIsAutosavePaused,
    saveStatus,
    handleAutosave,
    shouldWarnBeforeLeaving,
  };
}
